import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// Phase 2C-4 · Controlled Administrative Amendment dialog (SO + ESO).
// Shows ONLY the allowlisted reference fields — never the full commercial
// edit form. Relies on backend `controls.can_admin_amend` for gating and the
// backend allowlist for enforcement (frontend visibility is not security).
const SO_HEADER = [
  { k: "po_number", label: "PO Number", type: "text" },
  { k: "delivery_date", label: "Delivery Date", type: "date" },
  { k: "priority", label: "Priority", type: "select", opts: ["Low", "Normal", "High", "Critical"] },
  { k: "special_instructions", label: "Special Instructions", type: "textarea" },
];
const ESO_HEADER = [
  { k: "buyer_po_no", label: "Buyer PO Number", type: "text" },
  { k: "buyer_po_date", label: "Buyer PO Date", type: "date" },
  { k: "pi_no", label: "PI Number", type: "text" },
  { k: "pi_date", label: "PI Date", type: "date" },
  { k: "delivery_date", label: "Delivery Date", type: "date" },
  { k: "remarks", label: "Remarks", type: "textarea" },
];

export default function AdminAmendDialog({ open, onClose, orderType, orderId, onSaved }) {
  const isESO = orderType === "ESO";
  const base = isESO ? "/exports/orders" : "/sales-orders";
  const [order, setOrder] = useState(null);
  const [header, setHeader] = useState({});
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true); setReason("");
    (async () => {
      try {
        const { data: o } = await api.get(`${base}/${orderId}`);
        setOrder(o);
        const flds = isESO ? ESO_HEADER : SO_HEADER;
        const h = {}; flds.forEach(f => { h[f.k] = o[f.k] ?? ""; });
        setHeader(h);
        setLines((o.items || []).map(it => ({
          line_id: it.line_id || "", article_no: it.article_no || "",
          label: it.article_no || it.style_no || it.line_id || "line",
          delivery_date: it.delivery_date || "",
          customer_po_no: it.customer_po_no ?? "",
        })));
      } catch (e) {
        toast.error("Could not load order for amendment");
        onClose?.();
      } finally { setLoading(false); }
    })();
  }, [open, orderId]); // eslint-disable-line

  if (!open) return null;
  const controls = order?.controls || {};
  const canAmend = !!controls.can_admin_amend;
  const isSC = !!order?.lifecycle_state?.is_short_closed;

  const submit = async () => {
    if (reason.trim().length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    const payload = {
      reason: reason.trim(),
      header: { ...header },
      lines: lines.map(l => {
        const line = { line_id: l.line_id, article_no: l.article_no, delivery_date: l.delivery_date };
        if (!isESO) line.customer_po_no = l.customer_po_no;
        return line;
      }),
    };
    // Short-close: drop delivery-date keys the backend would reject.
    if (isSC) {
      delete payload.header.delivery_date;
      payload.lines = payload.lines.map(({ delivery_date, ...rest }) => rest);
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`${base}/${orderId}/admin-amend`, payload);
      toast.success(data?.changed ? "Administrative amendment saved" : "No changes to save");
      onSaved?.(data?.order);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amendment failed");
    } finally { setBusy(false); }
  };

  const setH = (k, v) => setHeader(h => ({ ...h, [k]: v }));
  const setL = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const flds = isESO ? ESO_HEADER : SO_HEADER;
  const inputCls = "w-full h-9 px-2 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm";

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4" data-testid="admin-amend-overlay">
      <div className="w-full max-w-lg max-h-[88vh] overflow-auto bg-[var(--bg-1)] border border-[var(--border)] rounded-md p-5 shadow-2xl" data-testid="admin-amend-dialog">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold" data-testid="admin-amend-title">Administrative Amendment</h3>
          <button onClick={onClose} className="text-sm opacity-70 hover:opacity-100" data-testid="admin-amend-close">✕</button>
        </div>
        <p className="text-xs opacity-70 mb-3">
          Reference / non-commercial fields only. Authorisation and quantities are unaffected.
          {isSC && <span className="text-amber-400"> This order is Short Closed — delivery-date changes are disabled.</span>}
        </p>

        {loading ? <div className="py-8 text-center text-sm opacity-70">Loading…</div> : (
          <>
            {!canAmend && (
              <div className="mb-3 text-sm text-red-400" data-testid="admin-amend-blocked">
                This order cannot be administratively amended in its current state.
              </div>
            )}
            <div className="space-y-3">
              {flds.map(f => (
                <div key={f.k} className={isSC && f.k === "delivery_date" ? "opacity-40 pointer-events-none" : ""}>
                  <label className="block text-xs mb-1 opacity-80">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea data-testid={`admin-amend-${f.k}`} value={header[f.k] || ""} rows={2}
                      onChange={e => setH(f.k, e.target.value)} className={inputCls + " h-auto py-1.5"} />
                  ) : f.type === "select" ? (
                    <select data-testid={`admin-amend-${f.k}`} value={header[f.k] || "Normal"}
                      onChange={e => setH(f.k, e.target.value)} className={inputCls}>
                      {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`admin-amend-${f.k}`} type={f.type} value={header[f.k] || ""}
                      onChange={e => setH(f.k, e.target.value)} className={inputCls} />
                  )}
                </div>
              ))}

              {lines.length > 0 && (
                <div className="border-t border-[var(--border)] pt-2">
                  <div className="text-xs opacity-70 mb-2">Line-level (delivery date{!isESO ? " / customer PO" : ""})</div>
                  {lines.map((l, i) => (
                    <div key={l.line_id || l.article_no || i} className="flex gap-2 items-center mb-2" data-testid={`admin-amend-line-${i}`}>
                      <span className="text-xs w-24 truncate opacity-80">{l.label}</span>
                      <input type="date" value={l.delivery_date || ""} disabled={isSC}
                        onChange={e => setL(i, "delivery_date", e.target.value)}
                        data-testid={`admin-amend-line-date-${i}`} className={inputCls} />
                      {!isESO && (
                        <input type="text" placeholder="Cust PO" value={l.customer_po_no || ""}
                          onChange={e => setL(i, "customer_po_no", e.target.value)}
                          data-testid={`admin-amend-line-cpo-${i}`} className={inputCls} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-xs mb-1 opacity-80">Reason <span className="text-red-400">*</span></label>
                <input data-testid="admin-amend-reason" type="text" value={reason}
                  onChange={e => setReason(e.target.value)} placeholder="Min 4 characters" className={inputCls} />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm" data-testid="admin-amend-cancel">Cancel</button>
              <button onClick={submit} disabled={busy || !canAmend || reason.trim().length < 4}
                className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-sm disabled:opacity-40"
                data-testid="admin-amend-save">{busy ? "Saving…" : "Save Amendment"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
